import React, { useState, useEffect, useMemo, useRef } from 'react'
import BrandConfigurationReportTab from './brandConfigurationTab';
import { capitalizeAllFirstLetters, capitalizeFirstLetter, formateDate, formateDateWithMonth, formateDateWithTime, getCompany, removeHyphens, showPrice, SpecialCharacter } from '../../helpers/commonFunctions';
import CustomPagination from '../../components/company_panel/common/custompagination';
import Loader from '../../components/company_panel/common/loader';
import notFound from '../../../src/assets/company/img/no-record-found.png'
import CustomRangepicker from '../../components/company_panel/common/rangepicker';
import { LOADER_TIME, SOCKET_EVENTS } from '../../utils/Constants';
import { SOCKET_CLIENT } from '../../utils/socket';
import { ExportBrandDiscountReport, ExportBrandPaymentReport, ExportBrandRefundReport, ExportBrandStockMovementReport, ExportBrandWasteReport, ListBrandDiscountReport, ListBrandPaymentReport, ListBrandRefundReport, ListBrandStockMovementReport, ListBrandWasteReport, ViewBrandDiscountReport, ViewBrandRefundReport } from '../../services/company_panel/brandReport.services';
import { GetBrandOutletsList } from '../../services/company_panel/outletService';
import { GetBrandsHavingOutlets } from '../../services/company_panel/brandService';
import { AllWasteReason, WasteDetails } from '../../services/company_panel/wasteManagement.services';
import { TagPicker } from 'rsuite';
import Swal from 'sweetalert2';
import { Modal } from 'react-bootstrap';

// Discount, refund, waste, payment and stock movement brand reports: the same
// filters (brands, outlets, business date, global search) and the same paginated
// { rows, count } response, so one list serves them all and each report only names
// its tab, API, columns and (optionally) its detail view and export. Rows come back
// ordered by business date, newest first; there is no sorting.

const amount = (value) => (value != null ? showPrice(value) + " SAR" : "-");
const text = (value) => (value != null && String(value).trim() !== '' ? capitalizeFirstLetter(String(value).trim()) : "-");
const dateTime = (value) => (value ? formateDateWithTime(value) : "-");
const couponName = (row) => row?.coupon_name ? capitalizeFirstLetter(row.coupon_name) : "-";
// enum-ish values arrive as table_service / raw-material; show them as words
const label = (value) => (value ? capitalizeAllFirstLetters(SpecialCharacter(String(value))) : "-");
const outletName = (row) => row?.outlet_name ? `${capitalizeFirstLetter(row.outlet_name)}${row?.outlet_short_code ? ` (${row.outlet_short_code})` : ''}` : "-";
const brandName = (row) => row?.brand_name ? capitalizeAllFirstLetters(row.brand_name) : "-";
const businessDate = (row) => row?.business_date ? formateDateWithMonth(row.business_date) : "-";
// stock quantities arrive as decimal strings; a missing one means nothing moved
const qty = (value) => {
    const number = Number(value);
    return value != null && value !== '' && !isNaN(number) ? number.toFixed(2) : "0.00";
};

// Both view APIs return the same nested order, so it is rendered the same way:
// who the order was, then how its money adds up. service_type is an object when
// the order has one (table service) and null otherwise, so it gets its own group
// rather than being squeezed into a single line.
const ORDER_FIELDS = [
    { label: 'Order ID', value: (order) => order?.id != null ? `#${order.id}` : "-" },
    { label: 'Status', value: (order) => label(order?.order_status) },
    { label: 'Source', value: (order) => label(order?.order_source) },
    { label: 'Customer', value: (order) => text(order?.customer_name) },
    { label: 'Business Date', value: (order) => businessDate(order) },
    { label: 'Created At', value: (order) => dateTime(order?.created_at) },
];

const SERVICE_FIELDS = [
    { label: 'Service Type', value: (service) => label(service?.service_type) },
    { label: 'Table', value: (service) => text(service?.table_name) },
    { label: 'Table Number', value: (service) => service?.table_number != null ? String(service.table_number) : "-" },
    { label: 'Guests', value: (service) => service?.guests_count != null ? String(service.guests_count) : "-" },
];

// Read as a bill, not as scattered tiles: the lines first, the net amount last.
const AMOUNT_FIELDS = [
    { label: 'Order Amount', key: 'order_amount' },
    { label: 'Sub Total', key: 'sub_total_price' },
    { label: 'Discount', key: 'discount_price' },
    { label: 'Tax', key: 'tax_total_price' },
    { label: 'Surcharge', key: 'surcharge_price' },
    { label: 'Payable Amount', key: 'payable_amount' },
];

const REPORTS = {
    discount: {
        tab: 'discount',
        pageRoute: [{ name: 'brand-discount', path: '/company/brand-report/discount/list' }],
        fetchReport: ListBrandDiscountReport,
        columns: [
            { label: 'Business Date', width: '13%', value: businessDate },
            { label: 'Order ID', width: '9%', value: (row) => row?.order_id != null ? `#${row.order_id}` : "-" },
            { label: 'Brand', width: '14%', value: brandName },
            { label: 'Outlet', width: '18%', value: outletName },
            { label: 'Coupon Name', width: '16%', value: couponName },
            { label: 'Coupon ID', width: '8%', value: (row) => row?.coupon_id != null ? `#${row.coupon_id}` : "-" },
            { label: 'Discount Amount', width: '13%', end: true, value: (row) => amount(row?.discount_amount) },
            { label: 'Net Amount', width: '13%', end: true, value: (row) => amount(row?.net_amount) },
        ],
        // Built in the background; the file arrives over the socket and
        // companyLayout downloads it, so this page only shows progress.
        exportReport: ExportBrandDiscountReport,
        exportEvents: SOCKET_EVENTS.REPORTS.DISCOUNT,
        exportLabel: 'Exporting discount report',
        fetchView: ViewBrandDiscountReport,
        viewTitle: 'Discount Detail',
        renderView: (detail) => (
            <OrderDetailView
                detail={detail}
                headline={{ label: 'Discount Amount', value: amount(detail?.discount_amount) }}
                groupTitle="Discount"
                groupFields={[
                    { label: 'Coupon Name', value: couponName },
                    { label: 'Coupon ID', value: (row) => row?.coupon_id != null ? `#${row.coupon_id}` : "-" },
                    { label: 'Discount Amount', value: (row) => amount(row?.discount_amount) },
                    { label: 'Net Amount', value: (row) => amount(row?.net_amount) },
                ]}
            />
        ),
    },
    refund: {
        tab: 'refund',
        pageRoute: [{ name: 'brand-refund', path: '/company/brand-report/refund/list' }],
        fetchReport: ListBrandRefundReport,
        columns: [
            { label: 'Business Date', width: '14%', value: businessDate },
            { label: 'Order ID', width: '10%', value: (row) => row?.order_id != null ? `#${row.order_id}` : "-" },
            { label: 'Transaction ID', width: '12%', value: (row) => row?.transaction_id != null ? `#${row.transaction_id}` : "-" },
            { label: 'Brand', width: '16%', value: brandName },
            { label: 'Outlet', width: '18%', value: outletName },
            { label: 'Order Amount', width: '13%', end: true, value: (row) => amount(row?.order_amount) },
            { label: 'Refunded Amount', width: '13%', end: true, value: (row) => amount(row?.refunded_amount) },
        ],
        exportReport: ExportBrandRefundReport,
        exportEvents: SOCKET_EVENTS.REPORTS.REFUND,
        exportLabel: 'Exporting refund report',
        fetchView: ViewBrandRefundReport,
        viewTitle: 'Refund Detail',
        renderView: (detail) => (
            <OrderDetailView
                detail={detail}
                headline={{ label: 'Refunded Amount', value: amount(detail?.refunded_amount) }}
                groupTitle="Refund"
                groupFields={[
                    { label: 'Refund Type', value: (row) => label(row?.refund_type) },
                    { label: 'Refund Reason', value: (row) => text(row?.refund_reason) },
                    { label: 'Refunded At', value: (row) => dateTime(row?.refunded_time) },
                    { label: 'Transaction ID', value: (row) => row?.transaction_id ? String(row.transaction_id) : "-" },
                    { label: 'Payment Method', value: (row) => label(row?.payment_method) },
                    { label: 'Tender', value: (row) => text(row?.tender_name) },
                    { label: 'Order Amount', value: (row) => amount(row?.order_amount) },
                    { label: 'Refunded Amount', value: (row) => amount(row?.refunded_amount) },
                ]}
            />
        ),
    },
    waste: {
        tab: 'waste',
        pageRoute: [{ name: 'brand-waste', path: '/company/brand-report/waste/list' }],
        fetchReport: ListBrandWasteReport,
        columns: [
            { label: 'Business Date', width: '16%', value: businessDate },
            { label: 'Waste ID', width: '12%', value: (row) => row?.id != null ? `#${row.id}` : "-" },
            { label: 'Brand', width: '22%', value: brandName },
            { label: 'Outlet', width: '30%', value: outletName },
            { label: 'Amount Loss', width: '20%', end: true, value: (row) => amount(row?.amount_loss) },
        ],
        exportReport: ExportBrandWasteReport,
        exportEvents: SOCKET_EVENTS.REPORTS.WASTE,
        exportLabel: 'Exporting waste report',
        fetchView: WasteDetails,
        viewTitle: 'Waste Detail',
        viewSize: 'xl',
        renderView: (detail) => <WasteDetailView detail={detail} />,
    },
    payment: {
        tab: 'payment',
        pageRoute: [{ name: 'brand-payment', path: '/company/brand-report/payment/list' }],
        fetchReport: ListBrandPaymentReport,
        // Same four columns as the outlet Payment report; one block per business
        // date (see PaymentBlocks).
        columns: [
            { label: 'Payment Method' },
            { label: 'Payment Type' },
            { label: 'Total Amount' },
            { label: 'Total Orders' },
        ],
        renderBody: (rows) => <PaymentBlocks rows={rows} />,
        // One business date per call, newest first: the next older one comes from
        // sending next_business_date back as last_business_date, as you scroll.
        cursorPaging: true,
        exportReport: ExportBrandPaymentReport,
        exportEvents: SOCKET_EVENTS.REPORTS.PAYMENT,
        exportLabel: 'Exporting payment report',
    },
    stock_movement: {
        tab: 'stock_movement',
        pageRoute: [{ name: 'brand-stock-movement', path: '/company/brand-report/stock_movement/list' }],
        fetchReport: ListBrandStockMovementReport,
        // 25 columns: keep each on one line and let the table scroll sideways
        nowrap: true,
        columns: [
            { label: 'Business Date', value: businessDate },
            { label: 'Brand', value: brandName },
            { label: 'Outlet', value: outletName },
            { label: 'Inventory', value: (row) => text(row?.inventory_name) },
            { label: 'SKU', value: (row) => row?.inventory_sku ? row.inventory_sku : "-" },
            { label: 'Category', value: (row) => text(row?.category_name) },
            { label: 'Sub Category', value: (row) => text(row?.sub_category_name) },
            { label: 'UOM', value: (row) => row?.uom_code ? row.uom_code : "-" },
            { label: 'Unit Price', end: true, value: (row) => amount(row?.raw_material_price ?? 0) },
            { label: 'Opening Balance (QTY)', end: true, value: (row) => qty(row?.opening_qty) },
            { label: 'Direct Purchase (QTY)', end: true, value: (row) => qty(row?.purchase_qty) },
            { label: 'All Transfer In (QTY)', end: true, value: (row) => qty(row?.transfer_in_qty) },
            { label: 'Total (+)', end: true, value: (row) => qty(row?.total_in_qty) },
            { label: 'All Transfer Out (QTY)', end: true, value: (row) => qty(row?.transfer_out_qty) },
            { label: 'Wastage', end: true, value: (row) => qty(row?.wastage_qty) },
            { label: 'Ideal Sold (QTY)', end: true, value: (row) => qty(row?.ideal_sold_qty) },
            { label: 'Total (-)', end: true, value: (row) => qty(row?.total_out_qty) },
            { label: 'System on Hand', end: true, value: (row) => qty(row?.system_in_hand_qty) },
            { label: 'Actual Store Count', end: true, value: (row) => qty(row?.physical_remaining_count) },
            { label: 'Variance (QTY)', end: true, value: (row) => qty(row?.variance_qty) },
            { label: 'Actual Used (QTY)', end: true, value: (row) => qty(row?.actual_used_quantity) },
            { label: 'Variance Amount', end: true, value: (row) => amount(row?.variance_amount ?? 0) },
            { label: 'Wastage Cost', end: true, value: (row) => amount(row?.wastage_cost ?? 0) },
            { label: 'Actual Used Cost', end: true, value: (row) => amount(row?.actual_used_cost ?? 0) },
            { label: 'Standard Used Cost', end: true, value: (row) => amount(row?.standard_used_cost ?? 0) },
        ],
        exportReport: ExportBrandStockMovementReport,
        exportEvents: SOCKET_EVENTS.REPORTS.STOCK_MOVEMENT,
        exportLabel: 'Exporting stock movement report',
    },
};

const DetailGrid = ({ fields, data }) => (
    <div className="detail-grid">
        {fields.map(field => (
            <div key={field.label}>
                <div className="detail-label">{field.label}</div>
                <div className="detail-value">{field.value(data)}</div>
            </div>
        ))}
    </div>
);

const DetailSection = ({ title, children }) => (
    <section className="detail-section">
        <h6 className="detail-section-title">{title}</h6>
        {children}
    </section>
);

// Where the record sits, plus the one figure the report is about, at a glance.
const DetailSummary = ({ detail, headline }) => (
    <div className="detail-summary">
        <div className="detail-summary-meta">
            <div>
                <div className="detail-label">Brand</div>
                <div className="detail-value">{brandName(detail)}</div>
            </div>
            <div>
                <div className="detail-label">Outlet</div>
                <div className="detail-value">{outletName(detail)}</div>
            </div>
            <div>
                <div className="detail-label">Business Date</div>
                <div className="detail-value">{businessDate(detail)}</div>
            </div>
            <div>
                <div className="detail-label">Order ID</div>
                <div className="detail-value">{detail?.order_id != null ? `#${detail.order_id}` : "-"}</div>
            </div>
        </div>
        {headline ? (
            <div className="detail-summary-headline">
                <div className="detail-label">{headline.label}</div>
                <div className="detail-headline-value">{headline.value}</div>
            </div>
        ) : null}
    </div>
);

const AmountsTable = ({ order }) => (
    <div className="detail-bill">
        {AMOUNT_FIELDS.map(field => (
            <div className="detail-bill-row" key={field.key}>
                <span>{field.label}</span>
                <span>{amount(order?.[field.key])}</span>
            </div>
        ))}
        <div className="detail-bill-row total">
            <span>Net Amount</span>
            <span>{amount(order?.net_amount)}</span>
        </div>
    </div>
);

// Discount and refund detail: summary, the report's own fields, then the order
// behind it in three groups — the order itself, its service, and its money.
const OrderDetailView = ({ detail, headline, groupTitle, groupFields }) => {
    const order = detail?.order;
    const service = order?.service_type && typeof order.service_type === 'object' ? order.service_type : null;
    return (
        <>
            <DetailSummary detail={detail} headline={headline} />

            <DetailSection title={groupTitle}>
                <DetailGrid fields={groupFields} data={detail} />
            </DetailSection>

            {order ? (
                <div className="detail-columns">
                    <div>
                        <DetailSection title="Order">
                            <DetailGrid fields={ORDER_FIELDS} data={order} />
                        </DetailSection>
                        <DetailSection title="Service">
                            {service ? (
                                <DetailGrid fields={SERVICE_FIELDS} data={service} />
                            ) : (
                                <p className="detail-empty">{order?.service_type ? label(order.service_type) : 'No service details for this order.'}</p>
                            )}
                        </DetailSection>
                    </div>
                    <DetailSection title="Amounts">
                        <AmountsTable order={order} />
                    </DetailSection>
                </div>
            ) : (
                <DetailSection title="Order">
                    <p className="detail-empty">No order found for this record.</p>
                </DetailSection>
            )}
        </>
    );
};

// The API returns one block per business date (tenders already added up across
// outlets, with the day's totals), newest first. Laid out like the outlet Payment
// report: a header per business date, a row per tender, then that day's totals.
const PaymentBlocks = ({ rows }) => rows.map(block => (
    <React.Fragment key={String(block?.business_date)}>
        {/* Spacer row before each group */}
        <tr><td colSpan={4}>&nbsp;</td></tr>
        {/* Business date header row */}
        <tr style={{ backgroundColor: '#f0f0f0' }}>
            <td colSpan={4}><strong>Business Date: {block?.business_date ? formateDate(block.business_date) : "-"}</strong></td>
        </tr>
        {(block?.tenders || []).map((tender, index) => (
            <tr key={`${tender?.tender_name}-${tender?.payment_type}-${index}`}>
                <td>{capitalizeFirstLetter(tender?.tender_name)}</td>
                <td>{capitalizeFirstLetter(tender?.payment_type)}</td>
                <td>{Number(tender?.total_amount || 0).toFixed(2)}</td>
                <td>{tender?.total_orders ?? 0}</td>
            </tr>
        ))}
        {/* Subtotal row per date */}
        <tr style={{ backgroundColor: '#fafafa' }}>
            <td>&nbsp;</td>
            <td>&nbsp;</td>
            <td><strong>Total Amount: {Number(block?.total_amount || 0).toFixed(2)}</strong></td>
            <td><strong>Total Orders: {block?.total_orders ?? 0}</strong></td>
        </tr>
    </React.Fragment>
));

// The waste detail comes from the waste-management API, so it carries the whole
// record: a summary plus every wasted item. reason_for_waste is an id, and the
// reason list is per brand, so it is resolved from the record's own brand.
const WasteDetailView = ({ detail }) => {

    const [reasonMap, setReasonMap] = useState({});

    useEffect(() => {
        if (!detail?.brand_id) return;
        AllWasteReason({ brand_id: detail.brand_id }).then(data => {
            const map = {};
            (data?.data?.response || []).forEach(row => { map[row.id] = row.reason; });
            setReasonMap(map);
        }).catch(error => { console.log("error ====> ", error); });
    }, [detail?.brand_id]);

    const items = detail?.wasted_items || [];
    const total = items.reduce((sum, item) => sum + Number(item?.amount_loss || 0), 0);

    const itemName = (item) => {
        const name = item?.item_type === 'raw-material' ? item?.inventory_name : item?.menu_item_name;
        return name ? capitalizeAllFirstLetters(name) : "-";
    };
    const reasonName = (item) => {
        const reason = reasonMap[item?.reason_for_waste];
        return reason ? capitalizeAllFirstLetters(reason) : "-";
    };

    const summary = [
        { label: 'Id', value: detail?.id != null ? `#${detail.id}` : "-" },
        { label: 'Business Date', value: businessDate(detail) },
        // get-waste nests these, unlike the flat report rows
        { label: 'Brand', value: detail?.brand?.name ? capitalizeAllFirstLetters(detail.brand.name) : "-" },
        { label: 'Outlet', value: detail?.outlet?.outlet_name ? `${capitalizeFirstLetter(detail.outlet.outlet_name)}${detail.outlet.short_code ? ` (${detail.outlet.short_code})` : ''}` : "-" },
        { label: 'Amount Loss', value: amount(detail?.amount_loss) },
        { label: 'Created At', value: dateTime(detail?.created_at) },
    ];

    return (
        <>
            <div className="waste-detail-summary">
                {summary.map(field => (
                    <div key={field.label}>
                        <div className="text-muted" style={{ fontSize: '12px' }}>{field.label}</div>
                        <div style={{ fontWeight: 600, wordBreak: 'break-word' }}>{field.value}</div>
                    </div>
                ))}
            </div>

            <h6 className="detail-section-title">Wasted Items{items.length ? ` (${items.length})` : ''}</h6>
            <div className="table-responsive">
                <table className="table table-custom mb-0">
                    <thead>
                        <tr>
                            <th>Item Type</th>
                            <th>Item Name</th>
                            <th className="text-end">Quantity</th>
                            <th>Unit Of Usage</th>
                            <th className="text-end">Amount Loss</th>
                            <th>Reason</th>
                            <th>Order</th>
                            <th>Remarks</th>
                        </tr>
                    </thead>
                    <tbody>
                        {items.length ? items.map((item, index) => (
                            <tr key={item?.id ?? index}>
                                <td>{item?.item_type ? capitalizeAllFirstLetters(removeHyphens(item.item_type)) : "-"}</td>
                                <td style={{ fontWeight: 500 }}>{itemName(item)}</td>
                                <td className="text-end">{item?.quantity != null ? item.quantity : "-"}</td>
                                <td>{item?.uom ? capitalizeFirstLetter(item.uom) : "-"}</td>
                                <td className="text-end">{amount(item?.amount_loss)}</td>
                                <td>{reasonName(item)}</td>
                                <td>{item?.order_id ? `#order ${item.order_id}` : "-"}</td>
                                <td style={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>{item?.remarks ? item.remarks : "-"}</td>
                            </tr>
                        )) : (
                            <tr><td colSpan={8} className="text-center">No wasted items</td></tr>
                        )}
                    </tbody>
                    {items.length ? (
                        <tfoot>
                            <tr className="detail-total-row">
                                <td colSpan={4} className="text-end">Total</td>
                                <td className="text-end">{amount(total)}</td>
                                <td colSpan={3}></td>
                            </tr>
                        </tfoot>
                    ) : null}
                </table>
            </div>
        </>
    );
};

const BrandAdjustmentReportList = ({ report }) => {
    const { tab, pageRoute, fetchReport, columns, fetchView, viewTitle, viewSize, renderView, exportReport, exportEvents, exportLabel, nowrap, renderBody, cursorPaging } = REPORTS[report];

    const [list, setList] = useState([])
    const [dataLength, setDataLength] = useState()
    const [itemPerPage, setItemPerPage] = useState(10)
    const [page, setPage] = useState(1);
    const [loader, setLoader] = useState(true)
    const [search, setSearch] = useState({})
    const [globalSearch, setGlobalSearch] = useState("")
    const [resetdate, setResetDate] = useState(false);
    const [showView, setShowView] = useState(false);
    const [viewDetail, setViewDetail] = useState(null);
    const [viewLoader, setViewLoader] = useState(false);
    const [exporting, setExporting] = useState(false);
    const [exportProgress, setExportProgress] = useState(null);
    const exportJobIdRef = useRef(null);

    // Cursor paging (payment): what to send as last_business_date next, whether
    // the API has older days left, and a marker under the table that loads them.
    const [hasMore, setHasMore] = useState(false);
    const [loadingMore, setLoadingMore] = useState(false);
    const cursorRef = useRef(null);
    const loadingMoreRef = useRef(false);
    const requestRef = useRef(0);
    const sentinelRef = useRef(null);
    const [isBrandChanged, setIsBrandChanged] = useState(false);
    const [isOutletChanged, setIsOutletChanged] = useState(false);

    const [brandList, setBrandList] = useState([]);
    const [outletList, setOutletList] = useState([]);
    const [selectedBrandIds, setSelectedBrandIds] = useState([]);
    const [selectedOutletIds, setSelectedOutletIds] = useState([]);

    useMemo(() => {
        document.addEventListener('brand-changed', function () {
            setIsBrandChanged(prev => !prev);
        });
        document.addEventListener('outlet-changed', function () {
            setIsOutletChanged(prev => !prev);
        })
    }, []);

    useEffect(() => {
        const requestId = ++requestRef.current;
        setLoader(true)
        cursorRef.current = null;
        loadingMoreRef.current = false;
        setLoadingMore(false);
        setHasMore(false);
        // company comes from the token; it is never sent
        fetchReport(cursorPaging ? {
            search: JSON.stringify(search),
            global_search: globalSearch,
        } : {
            page,
            per_page: itemPerPage,
            search: JSON.stringify(search),
            global_search: globalSearch,
        }).then(data => {
            // a filter changed while this was loading: a newer request owns the list
            if (requestId !== requestRef.current) return;
            const response = data && data.data && data.data.response;
            const rows = response ? response.rows || [] : [];
            setList(rows);
            if (cursorPaging) {
                cursorRef.current = response?.next_business_date ?? null;
                setHasMore(Boolean(response?.has_more));
                setDataLength(rows.length);
            } else {
                setDataLength(response ? response.count : 0);
            }
            setTimeout(() => { setLoader(false) }, LOADER_TIME);
        }).catch(error => {
            if (requestId !== requestRef.current) return;
            console.log("error ====> ", error);
            setList([]);
            setDataLength(0);
            setTimeout(() => { setLoader(false) }, LOADER_TIME);
            Swal.fire({
                customClass: 'swal-wide',
                position: 'top-right',
                icon: 'error',
                title: error?.response?.data?.errors?.message || error?.response?.data?.message || error?.message,
                showConfirmButton: false,
                timer: 1500,
                toast: true,
            })
        })
    }, [fetchReport, cursorPaging, search, globalSearch, page, itemPerPage, isBrandChanged, isOutletChanged])

    // Next older business date, appended under what is already shown.
    const loadMore = () => {
        if (!cursorPaging || !cursorRef.current || loadingMoreRef.current) return;
        const requestId = requestRef.current;
        loadingMoreRef.current = true;
        setLoadingMore(true);
        fetchReport({
            search: JSON.stringify(search),
            global_search: globalSearch,
            last_business_date: cursorRef.current,
        }).then(data => {
            if (requestId !== requestRef.current) return;
            const response = data?.data?.response || {};
            const rows = response.rows || [];
            setList(prev => {
                const shown = new Set(prev.map(block => String(block?.business_date)));
                return prev.concat(rows.filter(block => !shown.has(String(block?.business_date))));
            });
            cursorRef.current = response.next_business_date ?? null;
            setHasMore(Boolean(response.has_more));
        }).catch(error => {
            if (requestId !== requestRef.current) return;
            console.log("error ====> ", error);
            // stop here rather than retrying in a loop; changing a filter starts over
            setHasMore(false);
            Swal.fire({ customClass: 'swal-wide', icon: 'error', title: error?.response?.data?.message || 'Could not load more', showConfirmButton: false, timer: 2000, toast: true, position: 'top-right' });
        }).finally(() => {
            if (requestId !== requestRef.current) return;
            loadingMoreRef.current = false;
            setLoadingMore(false);
        });
    };

    // Load the next day when the marker under the table comes into view. The
    // observer is rebuilt after every load, so while the page is still too short
    // to scroll it keeps filling until it can scroll or the days run out.
    useEffect(() => {
        if (!cursorPaging || !hasMore || loader || loadingMore || !sentinelRef.current) return;
        const observer = new IntersectionObserver((entries) => {
            if (entries.some(entry => entry.isIntersecting)) loadMore();
        }, { rootMargin: '200px 0px' });
        observer.observe(sentinelRef.current);
        return () => observer.disconnect();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [cursorPaging, hasMore, loader, loadingMore, list.length]);

    useEffect(() => {
        GetBrandsHavingOutlets(getCompany().company_id).then(data => {
            setBrandList(data && data.data && data.data.response ? data.data.response : []);
        }).catch(error => {
            console.log("error=====>", error)
        })
    }, [])

    const prepareSearch = (key, value) => {
        let sr = { ...search };
        if (Array.isArray(value)) {
            if (value.length > 0) { sr[key] = value; } else { delete sr[key]; }
        } else if (value && typeof value === 'object') {
            // the date picker sends {} when cleared
            if (value.start && value.end) { sr[key] = value; } else { delete sr[key]; }
        } else if (String(value).length > 0) {
            sr[key] = value;
        } else {
            delete sr[key];
        }
        setSearch(sr);
        setPage(1);
    }

    const handleBrandChange = (selectedBrands) => {
        const brands = selectedBrands || [];
        setSelectedBrandIds(brands);
        setPage(1);

        if (brands.length === 0) {
            setOutletList([]);
            setSelectedOutletIds([]);
            const sr = { ...search };
            delete sr.brand_ids;
            delete sr.outlet_ids;
            setSearch(sr);
            return;
        }

        GetBrandOutletsList({ brand_ids: JSON.stringify(brands) }).then(data => {
            const outlets = data && data.data && data.data.response ? data.data.response : [];
            setOutletList(outlets);
            const validOutletIds = outlets.map(o => o.id);
            const filteredSelectedOutlets = selectedOutletIds.filter(id => validOutletIds.includes(id));
            setSelectedOutletIds(filteredSelectedOutlets);

            const sr = { ...search };
            sr.brand_ids = brands;
            if (filteredSelectedOutlets.length > 0) {
                sr.outlet_ids = filteredSelectedOutlets;
            } else {
                delete sr.outlet_ids;
            }
            setSearch(sr);
        }).catch(error => {
            console.log("error=====>", error)
        });
    }

    const handleOutletChange = (selectedOutlets) => {
        const outlets = selectedOutlets || [];
        setSelectedOutletIds(outlets);
        prepareSearch('outlet_ids', outlets);
    }

    const removeBrand = (brandId) => handleBrandChange(selectedBrandIds.filter(id => id !== brandId));
    const removeOutlet = (outletId) => handleOutletChange(selectedOutletIds.filter(id => id !== outletId));

    const brandData = brandList?.map(item => ({
        label: capitalizeAllFirstLetters(item?.name),
        value: item?.id
    })) || [];

    const filteredOutletData = outletList?.map(item => ({
        label: `${capitalizeAllFirstLetters(item?.outlet_name)}${item?.short_code ? ` (${item.short_code})` : ''}`,
        value: item?.id
    })) || [];

    // The download and the completed / failed toasts are handled globally in
    // companyLayout. This only tracks our own job, to drive the bar and button.
    useEffect(() => {
        if (!exportEvents) return;
        const finish = () => {
            setExporting(false);
            exportJobIdRef.current = null;
        };
        const handleExportSocket = (response) => {
            const data = response?.data;
            if (!data?.jobId) return;
            if (exportJobIdRef.current && data.jobId !== exportJobIdRef.current) return;

            if (response?.event === exportEvents.EXPORT_PROGRESS) {
                const percent = Number(data.percent) || 0;
                setExportProgress({ status: percent >= 100 ? 'completed' : 'progress', percent, processed: data.processed, total: data.total });
            } else if (response?.event === exportEvents.EXPORT_COMPLETED) {
                setExportProgress(prev => ({ ...(prev || {}), status: 'completed', percent: 100 }));
                finish();
                setTimeout(() => setExportProgress(null), 3000);
            } else if (response?.event === exportEvents.EXPORT_FAILED) {
                setExportProgress(null);
                finish();
            }
        };
        SOCKET_CLIENT.on('GET-DATA', handleExportSocket);
        return () => { SOCKET_CLIENT.off('GET-DATA', handleExportSocket); };
    }, [exportEvents]);

    // Same filters as the list, without paging: the file holds every matching row.
    const handleExport = () => {
        if (!exportReport) return;
        setExporting(true);
        setExportProgress({ status: 'progress', percent: 0 });
        exportReport({
            search: JSON.stringify(search),
            global_search: globalSearch,
        }).then(data => {
            const body = data?.data?.response || data?.data || {};
            const jobId = body.jobId || data?.data?.jobId;
            if (jobId) exportJobIdRef.current = jobId;
            Swal.fire({ customClass: 'swal-wide', icon: 'success', title: body.message || data?.data?.message || 'Export started', showConfirmButton: false, timer: 3000, toast: true, position: 'top-right' });
        }).catch(error => {
            console.log("error ====> ", error);
            Swal.fire({ customClass: 'swal-wide', icon: 'error', title: error?.response?.data?.errors?.message || error?.response?.data?.message || 'Export failed', showConfirmButton: false, timer: 3000, toast: true, position: 'top-right' });
            setExporting(false);
            setExportProgress(null);
            exportJobIdRef.current = null;
        });
    };

    // The list row carries only the report's own columns; the detail API adds the
    // order behind it, so the modal always loads fresh rather than reusing the row.
    const openView = (row) => {
        if (!fetchView || row?.id == null) return;
        setViewDetail(null);
        setViewLoader(true);
        setShowView(true);
        fetchView({ id: row.id }).then(data => {
            setViewDetail(data?.data?.response || null);
            setViewLoader(false);
        }).catch(error => {
            console.log("error ====> ", error);
            setViewLoader(false);
            setShowView(false);
            Swal.fire({
                customClass: 'swal-wide',
                icon: 'error',
                title: error?.response?.data?.message || 'Could not load the record',
                showConfirmButton: false,
                timer: 2000,
                toast: true,
                position: 'top-right',
            });
        });
    };

    const closeView = () => {
        setShowView(false);
        setViewDetail(null);
    };

    const resetFilter = (e) => {
        e.preventDefault()
        setGlobalSearch("")
        setSelectedBrandIds([]);
        setSelectedOutletIds([]);
        setOutletList([]);
        setSearch({})
        setResetDate(!resetdate)
        setPage(1);
    }

    return (
        <>
            <div className="animation_fade">
                <div className='page-header d-flex align-items-center'>
                    <div className='page-header-left'>
                        <h4>Brand Report</h4>
                    </div>
                    {exportReport ? (
                        <div className='page-header-right ms-auto'>
                            <button onClick={handleExport} disabled={!dataLength || exporting} className="btn btn-primary ms-2">{exporting ? 'Exporting...' : 'Export Report'}</button>
                        </div>
                    ) : null}
                </div>
                {exportProgress && (() => {
                    const isDone = exportProgress.status === 'completed';
                    const percent = exportProgress.percent || 0;
                    const accent = isDone ? '#22c55e' : '#2f9bff';
                    return (
                        <div style={{
                            display: 'flex', alignItems: 'center', gap: '16px',
                            background: '#fff', border: '1px solid #e7ecf3', borderLeft: `4px solid ${accent}`,
                            borderRadius: '12px', padding: '14px 18px', marginBottom: '16px',
                            boxShadow: '0 2px 10px rgba(16, 30, 54, 0.06)'
                        }}>
                            <div style={{
                                width: '40px', height: '40px', flexShrink: 0, borderRadius: '50%',
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                background: isDone ? 'rgba(34,197,94,0.12)' : 'rgba(47,155,255,0.12)', color: accent
                            }}>
                                {isDone ? (
                                    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6L9 17l-5-5" /></svg>
                                ) : (
                                    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" style={{ animation: 'spin 0.9s linear infinite' }}><path d="M21 12a9 9 0 1 1-6.219-8.56" /></svg>
                                )}
                            </div>
                            <div style={{ flex: 1, minWidth: 0 }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: '8px' }}>
                                    <span style={{ fontWeight: 600, color: '#1f2d3d', fontSize: '14px' }}>
                                        {isDone ? 'Export completed' : exportLabel}
                                        {exportProgress.total ? (
                                            <span style={{ fontWeight: 400, color: '#8a97a8', marginLeft: '8px', fontSize: '13px' }}>
                                                {(exportProgress.processed || 0).toLocaleString()} / {Number(exportProgress.total).toLocaleString()} records
                                            </span>
                                        ) : null}
                                    </span>
                                    <span style={{ fontWeight: 700, color: accent, fontSize: '14px', whiteSpace: 'nowrap' }}>{percent}%</span>
                                </div>
                                <div style={{ height: '8px', background: '#eef2f7', borderRadius: '999px', overflow: 'hidden' }}>
                                    <div style={{
                                        height: '100%', width: `${percent}%`, borderRadius: '999px',
                                        background: isDone ? accent : 'linear-gradient(90deg, #2f9bff, #6cb8ff)',
                                        transition: 'width 0.4s ease'
                                    }} />
                                </div>
                            </div>
                            <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
                        </div>
                    );
                })()}
                <div className="card custom-card overflow-hidden">
                    <div className='gap-20 border-bottom'>
                        <div className='col-12'>
                            <div className='d-flex justify-content-between align-items-center'>
                                <BrandConfigurationReportTab tab={tab} />
                            </div>
                        </div>
                    </div>
                    <div className="gap-20">
                        <div className='page-header d-flex align-items-center mb-0 flex-wrap'>
                            <div className='d-flex gap-3 align-items-center'>
                                <div className="form-group mb-0 rangepicker_container">
                                    <input type="search" className="form-control" value={globalSearch} placeholder="Search" onChange={(e) => { setGlobalSearch(e.target.value); setPage(1); }} />
                                </div>
                            </div>
                            <div className='page-header-right ms-auto'>
                                <div className='d-flex gap-3 align-items-center flex-wrap'>
                                    <div className="form-group mb-0 multi-filter-picker" style={{ width: '200px' }}>
                                        <TagPicker
                                            data={brandData}
                                            value={selectedBrandIds}
                                            onChange={handleBrandChange}
                                            placeholder="Select Brands"
                                            className='form-control form-control-select'
                                            cleanable={false}
                                            menuStyle={{ maxHeight: 200, overflowY: 'auto' }}
                                            renderValue={(value, items) => {
                                                const count = items.length;
                                                if (count === 0) return null;
                                                if (count === 1) {
                                                    return <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{items[0]?.label}</span>;
                                                }
                                                return <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>{count} brands selected</span>;
                                            }}
                                        />
                                    </div>
                                    <div className="form-group mb-0 multi-filter-picker" style={{ width: '200px' }}>
                                        <TagPicker
                                            data={filteredOutletData}
                                            value={selectedOutletIds}
                                            onChange={handleOutletChange}
                                            placeholder="Select Outlets"
                                            className='form-control form-control-select'
                                            cleanable={false}
                                            menuStyle={{ maxHeight: 200, overflowY: 'auto' }}
                                            renderValue={(value, items) => {
                                                const count = items.length;
                                                if (count === 0) return null;
                                                if (count === 1) {
                                                    return <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{items[0]?.label}</span>;
                                                }
                                                return <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>{count} outlets selected</span>;
                                            }}
                                        />
                                    </div>
                                    <div className="date-input">
                                        <CustomRangepicker business_date={true} GetDateRange={(e) => { prepareSearch("business_date", e) }} resetdate={resetdate} />
                                    </div>
                                    <button type="reset" value='Reset' onClick={resetFilter} className="btn btn-primary mr-2">Reset Filter</button>
                                </div>
                            </div>
                        </div>
                        {selectedBrandIds.length > 0 && (
                            <div className='d-flex flex-wrap gap-2 mt-2'>
                                <span className='text-muted me-1 align-self-center'>Brands:</span>
                                {selectedBrandIds.map(id => {
                                    const brand = brandList.find(b => b.id === id);
                                    if (!brand) return null;
                                    return (
                                        <span key={`b-${id}`} style={{ display: 'inline-flex', alignItems: 'center', backgroundColor: '#2f9bff', color: '#fff', borderRadius: '14px', padding: '4px 10px', fontSize: '13px', fontWeight: 500 }}>
                                            <span>{capitalizeAllFirstLetters(brand.name)}</span>
                                            <button type='button' aria-label='Remove' onClick={() => removeBrand(id)} style={{ background: 'transparent', border: 'none', cursor: 'pointer', marginLeft: 6, padding: 0, lineHeight: 1, color: '#fff', display: 'inline-flex', alignItems: 'center' }}>
                                                <svg width='12' height='12' viewBox='0 0 16 16' fill='currentColor' aria-hidden='true'><path d='M2.784 2.089l.069.058 5.146 5.147 5.146-5.147a.5.5 0 01.765.638l-.058.069L8.705 8l5.147 5.146a.5.5 0 01-.638.765l-.069-.058-5.146-5.147-5.146 5.147a.5.5 0 01-.765-.638l.058-.069L7.293 8 2.146 2.854a.5.5 0 01.638-.765z'></path></svg>
                                            </button>
                                        </span>
                                    );
                                })}
                            </div>
                        )}
                        {selectedOutletIds.length > 0 && (
                            <div className='d-flex flex-wrap gap-2 mt-2'>
                                <span className='text-muted me-1 align-self-center'>Outlets:</span>
                                {selectedOutletIds.map(id => {
                                    const outlet = outletList.find(o => o.id === id);
                                    if (!outlet) return null;
                                    return (
                                        <span key={`o-${id}`} style={{ display: 'inline-flex', alignItems: 'center', backgroundColor: '#e7f1ff', color: '#1565c0', border: '1px solid #b6d4fe', borderRadius: '14px', padding: '4px 10px', fontSize: '13px', fontWeight: 500 }}>
                                            <span>{capitalizeAllFirstLetters(outlet.outlet_name)}{outlet.short_code ? ` (${outlet.short_code})` : ''}</span>
                                            <button type='button' aria-label='Remove' onClick={() => removeOutlet(id)} style={{ background: 'transparent', border: 'none', cursor: 'pointer', marginLeft: 6, padding: 0, lineHeight: 1, color: '#1565c0', display: 'inline-flex', alignItems: 'center' }}>
                                                <svg width='12' height='12' viewBox='0 0 16 16' fill='currentColor' aria-hidden='true'><path d='M2.784 2.089l.069.058 5.146 5.147 5.146-5.147a.5.5 0 01.765.638l-.058.069L8.705 8l5.147 5.146a.5.5 0 01-.638.765l-.069-.058-5.146-5.147-5.146 5.147a.5.5 0 01-.765-.638l.058-.069L7.293 8 2.146 2.854a.5.5 0 01.638-.765z'></path></svg>
                                            </button>
                                        </span>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                    <div className="tabler-wrap">
                        <div className="table-responsive">
                            <table className={`table table-custom${nowrap ? ' text-nowrap' : ''}`}>
                                <thead>
                                    <tr>
                                        {columns.map(column => (
                                            <th key={column.label} className={column.end ? 'text-end' : ''} width={column.width}>{column.label}</th>
                                        ))}
                                        {fetchView ? <th className="text-end" width="8%">Action</th> : null}
                                    </tr>
                                </thead>
                                <tbody>
                                    {!loader && list?.length ? (
                                        <>
                                            {renderBody ? renderBody(list) : list.map((row, index) => (
                                                <tr key={row?.id ?? index}>
                                                    {columns.map(column => (
                                                        <td key={column.label} className={column.end ? 'text-end' : ''}>{column.value(row)}</td>
                                                    ))}
                                                    {fetchView ? (
                                                        <td className="text-end">
                                                            <button type="button" className="btn btn-icon btn-secondary" title="View details" onClick={() => openView(row)}>
                                                                <i className="ti ti-eye"></i>
                                                            </button>
                                                        </td>
                                                    ) : null}
                                                </tr>
                                            ))}
                                        </>
                                    ) : null}
                                </tbody>
                            </table>
                            {/* Outside the table so it centres in the visible width: a wide
                                report (stock movement) is far wider than the screen, and a
                                full-width cell would put the loader and empty state off to the
                                right. Sticky keeps them in view while scrolling sideways. */}
                            {cursorPaging && !loader && list?.length ? (
                                <div style={{ position: 'sticky', left: 0 }}>
                                    {loadingMore ? <Loader /> : null}
                                    {hasMore ? <div ref={sentinelRef} style={{ height: '1px' }} /> : (
                                        <p className="text-center text-muted mb-0 py-3" style={{ fontSize: '13px' }}>All business dates loaded</p>
                                    )}
                                </div>
                            ) : null}
                            {loader ? (
                                <div style={{ position: 'sticky', left: 0 }}><Loader /></div>
                            ) : !list?.length ? (
                                <div className="text-center py-3" style={{ position: 'sticky', left: 0 }}>
                                    <img src={notFound} style={{ width: '5%' }} alt="Not Found" />
                                    <p>No record found</p>
                                </div>
                            ) : null}
                        </div>
                        {/* cursor paging has no pages or total: rows load as you scroll */}
                        {cursorPaging ? null : (
                            <div className="tabler-footer">
                                <div className="d-flex align-items-center">
                                    <div id="example1_info" role="status" className="table-result" aria-live="polite">
                                        <b>Total Records: {dataLength ? dataLength : '0'}</b>
                                    </div>
                                    {dataLength && dataLength > 0 ? (
                                        <>
                                            <div className='ms-3'>
                                                Show
                                                <select
                                                    value={itemPerPage}
                                                    onChange={(e) => { setItemPerPage(Number(e.target.value)); setPage(1); }}
                                                    className="mx-2"
                                                >
                                                    <option value={10}>10</option>
                                                    <option value={25}>25</option>
                                                    <option value={50}>50</option>
                                                    <option value={100}>100</option>
                                                </select>
                                                entries
                                            </div>
                                            <CustomPagination
                                                datalength={dataLength}
                                                itemperpage={itemPerPage}
                                                setPage={setPage}
                                                currentPage={page}
                                                pageRoute={pageRoute}
                                            />
                                        </>
                                    ) : ''}
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            <Modal show={showView} onHide={closeView} size={viewSize || 'lg'} centered scrollable>
                <Modal.Header closeButton>
                    <Modal.Title style={{ fontSize: '1.25rem' }}>
                        {viewTitle}
                        {viewDetail?.id != null ? <small className="text-muted ms-2">#{viewDetail.id}</small> : null}
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body style={{ maxHeight: '75vh' }}>
                    {viewLoader ? <Loader /> : viewDetail ? (
                        renderView(viewDetail)
                    ) : <p className="text-center text-muted mb-0">No record found</p>}
                </Modal.Body>
                <Modal.Footer>
                    <button type="button" className="btn btn-outline-primary" onClick={closeView}>Close</button>
                </Modal.Footer>
            </Modal>
        </>
    );
}

export const BrandDiscountReportList = () => <BrandAdjustmentReportList report="discount" />;
export const BrandRefundReportList = () => <BrandAdjustmentReportList report="refund" />;
export const BrandWasteReportList = () => <BrandAdjustmentReportList report="waste" />;
export const BrandPaymentReportList = () => <BrandAdjustmentReportList report="payment" />;
export const BrandStockMovementReportList = () => <BrandAdjustmentReportList report="stock_movement" />;
